const RETURN_URL_PREFIX = '?returnurl=';
const DEFAULT_RETURN_URL = '/account';

/**
 * true if the value contains ASCII control characters (tab, newline, etc.).
 * browsers silently strip tab / newline from URLs, so '/\t/evil.com' is navigated as '//evil.com'
 */
const hasControlChars = (value: string) => {
    for(let i = 0; i < value.length; i++){
        const code = value.charCodeAt(i);
        if(code <= 0x1F || code === 0x7F) return true;
    }
    return false;
};

/**
 * true if the value starts with exactly one '/', not followed by another '/' or '\'.
 * '//host' and '/\host' are protocol-relative URLs that leave the site.
 */
const isSingleSlashPath = (value: string) => /^\/(?![/\\])/.test(value);

/**
 * Prevent open redirect: returns a same-origin relative URL (path + query + hash), or null.
 *
 * The return URL may arrive encoded (server: /login?returnurl=%2faccount%3fid%3d1)
 * or unencoded (getReturnURL(): /login?ReturnUrl=/page?playlist=1&x=2),
 * so everything after '?returnurl=' is taken as the value (not URLSearchParams, which would cut it at '&').
 *
 * Blocked, for example: //evil.com, /\evil.com, /%09/evil.com, /..//evil.com, https://evil.com, javascript:...
 */
export const getSafeReturnUrl = (search: string, origin: string): string | null => {

    if(!search || !search.toLowerCase().startsWith(RETURN_URL_PREFIX)) return null;

    let value: string;

    try{
        value = decodeURIComponent(search.substring(RETURN_URL_PREFIX.length));
    }
    catch{
        // malformed % sequence
        return null;
    }

    if(!isSingleSlashPath(value)) return null;

    // browsers treat '\' as '/', so any backslash can turn the value into '//host'
    if(value.includes('\\') || hasControlChars(value)) return null;

    try{
        // let the URL parser resolve it, and verify it stays on our origin
        const url = new URL(value, origin);
        if(url.origin !== origin) return null;

        const result = url.pathname + url.search + url.hash;

        // re-check after normalization: the parser resolves dot segments (also %2e),
        // so '/..//evil.com' or '/a/../..//evil.com' becomes '//evil.com' - protocol-relative again
        if(!isSingleSlashPath(result)) return null;

        // redirect to exactly what was validated
        return result;
    }
    catch{
        return null;
    }
};

/**
 * the URL to redirect to after login: a safe return URL, or the user account
 */
export const getRedirectAfterLoginUrl = (search: string, origin: string): string => {
    return getSafeReturnUrl(search, origin) ?? DEFAULT_RETURN_URL;
};
